//// Application Packages
import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { format, differenceInYears } from 'date-fns'
import { object, string } from 'yup'
import axios from 'axios'
import {
    translateMultiFormField,
    matchingEvaluationField,
    ssnRestrictToIntValueField,
    ssnPresentButNotBeyondMinimumField,
} from '../../functions/field-functions'
import {
    subtractValuesAdditionalArrayForm,
    extractSubmissionForm,
} from '../../functions/form-functions'

//// Application Slices
import { selectSettings } from '../../reducers/settingSlice'
import {
    updateFormData,
    updateFormValidity,
    updateFormAuxiliary,
    deleteFormData,
    updateFormSystem,
    selectForms,
} from '../../reducers/formSlice'

//// Application Components
import TooltipComponent from '../../components/simple-components/tooltip-component'
import AddFormComponent from '../../components/navigation-components/add-form-component'
import ButtonComponent from '../../components/conditional-components/button-component'
import LoadingComponent from '../../components/simple-components/loading-component'
import NotificationComponent from '../../components/conditional-components/notification-component'
import AdditionalNameAndPersonalDetailsComponent from '../../components/generic-components/additional-name-and-personal-details-component'

//// Carbon Components
import {
    Form,
    TextInput,
    Select,
    SelectItem,
    DatePicker,
    DatePickerInput,
} from '@carbon/react'

//// Carbon Icons

//// Static Constants

//////// Component
const NameAndPersonalDetailsForm = ({ instance }) => {
    //////// Hooks
    const dispatch = useDispatch()
    const navigate = useNavigate()

    //////// State
    const [loading, setLoading] = useState(false)
    const [notification, setNotification] = useState({})

    //////// Slices
    const settingsData = useSelector(selectSettings)
    const formsData = useSelector(selectForms)

    //// Slice Data
    const digital_name = settingsData?.['form']?.['digital_name']
    const category_digital_name =
        settingsData?.['form']?.['category_digital_name']
    const submit_route = settingsData?.['form']?.['submit_route']
    const fields = settingsData?.['form']?.['fields']
    const max_form_iterations =
        settingsData?.['application']?.['max_form_iterations']
    const configuration =
        settingsData?.configuration?.forms?.[category_digital_name]
    const xact_cde = settingsData?.['category']?.['xact_cde']

    //////// Search Params

    //////// Methods

    // Orchestrate Application Suitabilty (Integration)
    const orchestrateApplicationSuitability = async (formData) => {
        try {
            if (formData) {
                const base_url = `${process.env.REACT_APP_BASE_URL}`
                const api_endpoint = `${process.env.REACT_APP_ORCHESTRATION_ENDPOINT_PROXY_APPLICATION_SUITABILITY}`
                const url = base_url + api_endpoint
                const { tax_nbr } = formData
                const { clnt_cde } = configuration

                const { data } = await axios.post(url, {
                    tax_nbr: tax_nbr,
                    clnt_cde: clnt_cde,
                })

                if (data) {
                    return data
                } else {
                    console.log(
                        'Application Suitability: Response Is Undefined'
                    )
                    return null
                }
            } else {
                console.log('Application Suitability: Request Is Undefined')
                return null
            }
        } catch (error) {
            console.warn('Orchestrate Application Suitability Error:', error)
            return null
        }
    }

    // Generate Validation Schema
    const generateSchema = async (auxAddtForms) => {
        try {
            const dynamic = {}
            if (auxAddtForms?.length > 0) {
                dynamic.addtObjs = [
                    {
                        frst_nme: '',
                        sec_nme: '',
                        surnme: '',
                    },
                ]
                dynamic.addtFilds = [
                    {
                        frst_nme: false,
                        frst_nmeErrMsg: '',
                        sec_nme: false,
                        sec_nmeErrMsg: '',
                        surnme: false,
                        surnmeErrMsg: '',
                    },
                ]
                dynamic.addtObjs[0] = {
                    ...dynamic.addtObjs[0],
                    ttl: '',
                    frst_nme: '',
                    sec_nme: '',
                    surnme: '',
                    tax_nbr: '',
                    tax_nbrConfirm: '',
                    brth_dte: '',
                    sex: '',
                    race_cde: '',
                }
                dynamic.addtFilds[0] = {
                    ...dynamic.addtFilds[0],
                    ttl: '',
                    ttlErrMsg: '',
                    frst_nme: '',
                    frst_nmeErrMsg: '',
                    sec_nme: '',
                    sec_nmeErrMsg: '',
                    surnme: '',
                    surnmeErrMsg: '',
                    tax_nbr: '',
                    tax_nbrErrMsg: '',
                    tax_nbrConfirm: '',
                    tax_nbrConfirmErrMsg: '',
                    brth_dte: '',
                    brth_dteErrMsg: '',
                    sex: '',
                    sexErrMsg: '',
                    race_cde: '',
                    race_cdeErrMsg: '',
                }
                await auxAddtForms.forEach(async (el) => {
                    dynamic.addtObjs.push({
                        [`frst_nme` + el]: '',
                        [`sec_nme` + el]: '',
                        [`surnme` + el]: '',
                    })
                    dynamic.addtFilds.push({
                        [`frst_nme` + el]: false,
                        [`frst_nme` + el + 'ErrMsg']: '',
                        [`sec_nme` + el]: false,
                        [`sec_nme` + el + 'ErrMsg']: '',
                        [`surnme` + el]: false,
                        [`surnme` + el + 'ErrMsg']: '',
                    })
                })
                dynamic.addtObjsSchema = {}
                if (dynamic?.addtObjs?.length > 0) {
                    dynamic.addtObjs.forEach(async (el) => {
                        dynamic.addtObjsSchema = {
                            ...dynamic.addtObjsSchema,
                            ...el,
                        }
                    })
                }
                dynamic.addtFildsSchema = {}
                if (dynamic?.addtFilds?.length > 0) {
                    dynamic.addtFilds.forEach(async (el) => {
                        dynamic.addtFildsSchema = {
                            ...dynamic.addtFildsSchema,
                            ...el,
                        }
                    })
                }
                dynamic.addtObjsSchemaKeys = Object?.keys(
                    dynamic.addtObjsSchema
                )
                dynamic.addtFildsSchemaKeys = Object?.keys(
                    dynamic.addtFildsSchema
                )
                dynamic.addtSchema = {}
                if (dynamic?.addtObjsSchemaKeys?.length > 0) {
                    dynamic.addtObjsSchemaKeys.forEach(async (el) => {
                        if (el?.includes('ttl')) {
                            dynamic.addtSchema = {
                                ...dynamic.addtSchema,
                                [el]: string().min(0).max(4),
                            }
                        } else if (el?.includes('frst_nme')) {
                            dynamic.addtSchema = {
                                ...dynamic.addtSchema,
                                [el]: string()
                                    .min(0)
                                    .max(16)
                                    .required(
                                        'This field is required to be entered or selected.'
                                    ),
                            }
                        } else if (el?.includes('sec_nme')) {
                            dynamic.addtSchema = {
                                ...dynamic.addtSchema,
                                [el]: string().min(0).max(40),
                            }
                        } else if (el?.includes('surnme')) {
                            dynamic.addtSchema = {
                                ...dynamic.addtSchema,
                                [el]: string()
                                    .min(0)
                                    .max(40)
                                    .required(
                                        'This field is required to be entered or selected.'
                                    ),
                            }
                        } else if (el?.includes('sfx')) {
                            dynamic.addtSchema = {
                                ...dynamic.addtSchema,
                                [el]: string().min(0).max(4),
                            }
                        } else if (el?.includes('tax_nbr')) {
                            dynamic.addtSchema = {
                                ...dynamic.addtSchema,
                                [el]: string()
                                    .min(0)
                                    .max(9)
                                    .test('match', 'U.S. SSN must match', () =>
                                        matchingEvaluationField(
                                            formsData?.[digital_name]?.values
                                                ?.tax_nbr,
                                            formsData?.[digital_name]?.values
                                                ?.tax_nbrConfirm
                                        )
                                    )
                                    .test(
                                        'len',
                                        'Less than 9 digits have been entered for the Social Security Number. Please ensure 9 digits have been entered for the Social Security Number before saving progress.',
                                        () =>
                                            ssnPresentButNotBeyondMinimumField(
                                                formsData,
                                                digital_name,
                                                'tax_nbr'
                                            )
                                    ),
                            }
                        } else if (el?.includes('tax_nbrConfirm')) {
                            dynamic.addtSchema = {
                                ...dynamic.addtSchema,
                                [el]: string()
                                    .min(0)
                                    .max(9)
                                    .test('match', 'U.S. SSN must match', () =>
                                        matchingEvaluationField(
                                            formsData?.[digital_name]?.values
                                                ?.tax_nbrConfirm,
                                            formsData?.[digital_name]?.values
                                                ?.tax_nbr
                                        )
                                    )
                                    .test(
                                        'len',
                                        'Less than 9 digits have been entered for the Social Security Number. Please ensure 9 digits have been entered for the Social Security Number before saving progress.',
                                        () =>
                                            ssnPresentButNotBeyondMinimumField(
                                                formsData,
                                                digital_name,
                                                'tax_nbrConfirm'
                                            )
                                    ),
                            }
                        } else if (
                            el?.includes('sex') ||
                            el?.includes('race_cde')
                        ) {
                            dynamic.addtSchema = {
                                ...dynamic.addtSchema,
                                [el]: string(),
                            }
                        } else {
                            dynamic.addtSchema = {
                                ...dynamic.addtSchema,
                                [el]: string().required(
                                    'This field is required to be entered or selected.'
                                ),
                            }
                        }
                    })
                }
                dynamic.addtValid = {}
                if (dynamic?.addtFildsSchemaKeys?.length > 0) {
                    dynamic.addtFildsSchemaKeys.forEach(async (el) => {
                        if (el?.includes('ErrMsg')) {
                            dynamic.addtValid = {
                                ...dynamic.addtValid,
                                [el]: '',
                            }
                        } else {
                            dynamic.addtValid = {
                                ...dynamic.addtValid,
                                [el]: false,
                            }
                        }
                    })
                }
            }
            return dynamic
        } catch (error) {
            console.warn('Generate Schema Error:', error)
            return null
        }
    }

    // Validate Form Submissions
    const validation = async (submit) => {
        // Validate Form Submission Data Against Schema
        try {
            // Schema Object
            const schema = {}
            if (formsData[digital_name].auxiliary.addtForms?.length === 0) {
                schema.yup = object({
                    ttl: string().min(0).max(4),
                    frst_nme: string()
                        .min(0)
                        .max(16)
                        .required(
                            'This field is required to be entered or selected.'
                        ),
                    sec_nme: string().min(0).max(40),
                    surnme: string()
                        .min(0)
                        .max(40)
                        .required(
                            'This field is required to be entered or selected.'
                        ),
                    sfx: string().min(0).max(4),
                    tax_nbr: string()
                        .min(0)
                        .max(9)
                        .test('match', 'U.S. SSN must match', () =>
                            matchingEvaluationField(
                                formsData?.[digital_name]?.values?.tax_nbr,
                                formsData?.[digital_name]?.values
                                    ?.tax_nbrConfirm
                            )
                        )
                        .test(
                            'len',
                            'Less than 9 digits have been entered for the Social Security Number. Please ensure 9 digits have been entered for the Social Security Number before saving progress.',
                            () =>
                                ssnPresentButNotBeyondMinimumField(
                                    formsData,
                                    digital_name,
                                    'tax_nbr'
                                )
                        ),
                    tax_nbrConfirm: string()
                        .min(0)
                        .max(9)
                        .test('match', 'U.S. SSN must match', () =>
                            matchingEvaluationField(
                                formsData?.[digital_name]?.values
                                    ?.tax_nbrConfirm,
                                formsData?.[digital_name]?.values?.tax_nbr
                            )
                        )
                        .test(
                            'len',
                            'Less than 9 digits have been entered for the Social Security Number. Please ensure 9 digits have been entered for the Social Security Number before saving progress.',
                            () =>
                                ssnPresentButNotBeyondMinimumField(
                                    formsData,
                                    digital_name,
                                    'tax_nbrConfirm'
                                )
                        ),
                    brth_dte: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    sex: string(),
                    race_cde: string(),
                })
                schema.valid = {
                    ttl: false,
                    ttlErrMsg: '',
                    frst_nme: false,
                    frst_nmeErrMsg: '',
                    sec_nme: false,
                    sec_nmeErrMsg: '',
                    surnme: false,
                    surnmeErrMsg: '',
                    tax_nbr: false,
                    tax_nbrErrMsg: '',
                    tax_nbrConfirm: false,
                    tax_nbrConfirmErrMsg: '',
                    brth_dte: false,
                    brth_dteErrMsg: '',
                    sex: false,
                    sexErrMsg: '',
                    race_cde: false,
                    race_cdeErrMsg: '',
                }
            } else if (
                formsData[digital_name].auxiliary.addtForms?.length > 0
            ) {
                const addtForms = formsData[digital_name].auxiliary.addtForms
                const { addtSchema, addtValid } = await generateSchema(
                    addtForms
                )
                schema.yup = object().shape({ ...addtSchema })
                schema.valid = addtValid
            }
            await schema.yup.validate(
                { ...submit },
                { strict: true, abortEarly: false }
            )
            dispatch(
                updateFormValidity({
                    digital_name: digital_name,
                    errors: {},
                })
            )
            return true
        } catch (err) {
            const errors = {}
            if (err?.inner?.length > 0) {
                await err.inner.forEach(async (el) => {
                    if (el?.path) {
                        errors[el?.path] = true
                        if (el?.errors?.length > 0) {
                            const errName = el?.path + 'ErrMsg'
                            const errMsg = el?.errors?.join(' ')
                            const tran = translateMultiFormField(
                                fields,
                                max_form_iterations,
                                el?.path
                            )
                            if (tran) {
                                const tranErrMsg = await errMsg.replaceAll(
                                    el?.path,
                                    tran
                                )
                                errors[errName] = tranErrMsg
                            } else {
                                errors[errName] = el?.errors?.join(' ')
                            }
                        }
                    }
                })
            }
            //// Start Watson Assistant Journey

            //// End Watson Assistant Journey
            dispatch(
                updateFormValidity({
                    digital_name: digital_name,
                    errors: errors,
                })
            )
            return false
        }
    }

    // Re-Validate Data Submission
    const revalidation = async (event_name) => {
        try {
            // Update Validation State
            if (digital_name && event_name) {
                dispatch(
                    updateFormValidity({
                        digital_name: digital_name,
                        errors: {
                            [event_name]: false,
                            [event_name + 'ErrMsg']: '',
                        },
                    })
                )
            }
        } catch (error) {
            console.warn('Revalidation Error:', error)
            return null
        }
    }

    //////// Functional Methods

    // Handle Journey Alert
    const handleJourneyAlert = () => {
        if (instance?.tours) {
            instance.tours.startTour(
                'Registered Nursing Endorsement Name And Personal SSN Tour'
            )
        }
    }

    // Handle Removal Of Additional Name And Personal Details Component/s
    const handleSubtractAdditionalNameAndPersonalDetailsComponent = async (
        e
    ) => {
        try {
            if (e?.target?.name) {
                const addtForms = [
                    ...formsData[digital_name].auxiliary.addtForms,
                ]
                const subt = parseInt(e.target.name)
                const index = addtForms?.indexOf(subt)
                if (index > -1) {
                    addtForms.splice(index, 1)
                    dispatch(
                        updateFormAuxiliary({
                            digital_name: digital_name,
                            auxiliary: {
                                addtForms: addtForms,
                            },
                        })
                    )
                    const values = subtractValuesAdditionalArrayForm(
                        fields,
                        max_form_iterations,
                        formsData?.[digital_name]?.values,
                        index
                    )
                    if (values) {
                        dispatch(
                            deleteFormData({
                                digital_name: digital_name,
                            })
                        )
                        dispatch(
                            updateFormData({
                                digital_name: digital_name,
                                values: values,
                            })
                        )
                    }
                }
            }
        } catch (error) {
            console.warn(
                'Handle Subtract Additional Name And Personal Details Component Error:',
                error
            )
            return null
        }
    }

    // Handle Data Of All Fields + DatePicker (ID)
    const handleData = (e, id) => {
        if (
            e?.target?.name &&
            e?.target?.value &&
            id &&
            id.includes('tax_nbr')
        ) {
            const int = ssnRestrictToIntValueField(e?.target?.value)
            if (int) {
                dispatch(
                    updateFormData({
                        digital_name: digital_name,
                        values: {
                            [e.target.name]: e?.target?.value,
                        },
                    })
                )
                revalidation(e.target.name)
            }
        } else if (e?.target?.name && e?.target?.value) {
            dispatch(
                updateFormData({
                    digital_name: digital_name,
                    values: {
                        [e.target.name]: e.target.value,
                    },
                })
            )
            revalidation(e.target.name)
        } else if (e?.target?.name) {
            dispatch(
                updateFormData({
                    digital_name: digital_name,
                    values: { [e.target.name]: '' },
                })
            )
            revalidation(e.target.name)
        } else if (e?.length > 0 && id) {
            dispatch(
                updateFormData({
                    digital_name: digital_name,
                    values: {
                        [id]: format(new Date(e?.[0]), 'MM/dd/yyyy'),
                    },
                })
            )
            revalidation(id)
        }
        //// Start Watson Assistant Journey

        if (e?.length > 0 && id) {
            const olderThan18 =
                differenceInYears(new Date(), new Date(e?.[0])) >= 18
            if (!olderThan18) {
                instance.tours.startTour(
                    'Registered Nursing Endorsement Name And Personal Above 18 Tour'
                )
            } else if (olderThan18) {
                instance.tours.endTour()
            }
        }

        //// End Watson Assistant Journey
    }

    // Handle Form Submission
    const handleSubmit = async () => {
        try {
            console.info('Name And Personal Details Form: Submitted')

            setLoading(true)

            // Extract Form Data From Redux
            const submit = await extractSubmissionForm(digital_name, formsData)

            // Validate Form Submission Data
            const valid = await validation(submit)

            // Check If Form Data Is Valid
            if (valid) {
                console.info('Name And Personal Details Form: Passed Valid')

                // Check In Result Variable
                //const check = {}
                const suitable = {}

                // Check If Application Is Suitable Or If Suitability Is Not Required
                if (submit?.['tax_nbr']) {
                    // Send Orchestration Request
                    const orchestrate = await orchestrateApplicationSuitability(
                        submit
                    )

                    // Set Parameters To Check Variable
                    suitable.entity_checked = orchestrate?.entity_checked
                    suitable.entity_suitable = orchestrate?.entity_suitable

                    console.info(
                        'Name And Personal Details Form: Passed Orchestrate'
                    )
                } else {
                    // Set Parameters To Suitable Variable
                    suitable.entity_checked = true
                    suitable.entity_suitable = true

                    console.info(
                        'Name And Personal Details Form: Passed Orchestrate'
                    )
                }

                // Check For Orchestrate Response And Application Suitability Data
                /*if (check?.['entity_checked'] && check?.['entity_suitable']) {
                    // Set Parameters To Suitable Variable
                    suitable.entity_checked = true
                    suitable.entity_suitable = true
                } else {
                    suitable.entity_checked = false
                    suitable.entity_suitable = false
                }*/

                // Check For Orchestrate Response And Application Suitability Data
                if (
                    suitable?.['entity_checked'] &&
                    suitable?.['entity_suitable']
                ) {
                    dispatch(
                        updateFormSystem({
                            digital_name: digital_name,
                            system: { valid: true },
                        })
                    )
                    setLoading(false)
                    setNotification({
                        type: 'inline',
                        visible: false,
                        kind: '',
                        title: '',
                        subtitle: '',
                    })

                    // Navigate To Next Form
                    navigate(submit_route ? submit_route : '/')
                } else if (
                    suitable?.['entity_checked'] &&
                    !suitable?.['entity_suitable']
                ) {
                    console.info(
                        'Name And Personal Details Form: Failed Application Suitability'
                    )

                    dispatch(
                        updateFormSystem({
                            digital_name: digital_name,
                            system: { valid: false },
                        })
                    )
                    dispatch(
                        updateFormValidity({
                            digital_name: digital_name,
                            errors: {
                                tax_nbr: true,
                                tax_nbrErrMsg:
                                    'You Have Already Submitted An Application',
                                tax_nbrConfirm: true,
                                tax_nbrConfirmErrMsg:
                                    'You Have Already Submitted An Application',
                            },
                        })
                    )
                    setLoading(false)
                    setNotification({
                        type: 'inline',
                        visible: true,
                        kind: 'error',
                        title: 'Name And Personal Details Invalid:',
                        subtitle: 'You Have Already Submitted An Application',
                    })

                    console.log(
                        'Name And Personal Details Form: Existing Application'
                    )
                } else {
                    console.info(
                        'Name And Personal Details Form: This social security number has been used on a previously submitted application and cannot be used again'
                    )

                    dispatch(
                        updateFormSystem({
                            digital_name: digital_name,
                            system: { valid: false },
                        })
                    )
                    dispatch(
                        updateFormValidity({
                            digital_name: digital_name,
                            errors: {
                                tax_nbr: true,
                                tax_nbrErrMsg:
                                    'This social security number has been used on a previously submitted application and cannot be used again',
                                tax_nbrConfirm: true,
                                tax_nbrConfirmErrMsg:
                                    'This social security number has been used on a previously submitted application and cannot be used again',
                            },
                        })
                    )
                    setLoading(false)
                    setNotification({
                        type: 'inline',
                        visible: true,
                        kind: 'error',
                        title: 'Name And Personal Details Invalid:',
                        subtitle:
                            'This social security number has been used on a previously submitted application and cannot be used again',
                    })

                    console.log(
                        'Name And Personal Details Form: This social security number has been used on a previously submitted application and cannot be used again'
                    )
                }
            } else {
                console.info(
                    'Name And Personal Details Form: Previous Application Check Failed For Tax Id Number'
                )

                dispatch(
                    updateFormSystem({
                        digital_name: digital_name,
                        system: { valid: false },
                    })
                )
                setLoading(false)
                setNotification({
                    type: 'inline',
                    visible: true,
                    kind: 'error',
                    title: 'Form Errors:',
                    subtitle:
                        'There are errors on the form (such as missing required info). Please scroll up and update the fields with red messages.',
                })

                console.log(
                    'Name And Personal Details Failed Valid: There are errors on the form (such as missing required info). Please scroll up and update the fields with red messages.'
                )
            }
        } catch (error) {
            console.warn('Handle Submit Error:', error)
            return null
        }
    }

    // Handle Keyboard Enter As Submit (Method)
    const handleKeyDown = async (event) => {
        try {
            if (event?.key === 'Enter') {
                await handleSubmit()
            }
        } catch (error) {
            console.warn('Handle Down Key Error:', error)
            return null
        }
    }

    //////// Memoization

    //////// Effects/Side Effects

    if (xact_cde === '1010') {
        // Reload Watson Assistant Journey on Reload
        useEffect(() => {
            if (instance?.tours) {
                instance.tours.endTour()
                instance.tours.startTour(
                    'RN or LPN by Examination Personal Details Page Render'
                )
            }
            return () => {}
        }, [instance])
    } else if (xact_cde === '1016') {
        useEffect(() => {
            if (instance?.tours) {
                instance.tours.endTour()
                instance.tours.startTour(
                    'Registered Nursing Endorsement Name And Personal Tour'
                )
            }
            return () => {}
        }, [instance])
    }

    //////////////// Render & Return
    if (loading) {
        return (
            <>
                <Form
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                    onSubmit={handleSubmit}
                >
                    <LoadingComponent />
                </Form>
            </>
        )
    } else if (formsData[digital_name].auxiliary.addtForms?.length === 0) {
        return (
            <>
                <Form
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                    onSubmit={handleSubmit}
                >
                    <TextInput
                        labelText="Title:"
                        id="ttl"
                        name="ttl"
                        placeholder=""
                        value={formsData?.[digital_name]?.values?.ttl}
                        maxLength="4"
                        type="text"
                        onChange={handleData}
                        invalid={formsData?.[digital_name]?.errors?.ttl}
                        invalidText={
                            formsData?.[digital_name]?.errors?.ttlErrMsg
                        }
                    />

                    <TextInput
                        className="required"
                        labelText="First Name:"
                        id="frst_nme"
                        name="frst_nme"
                        placeholder=""
                        value={formsData?.[digital_name]?.values?.frst_nme}
                        maxLength="16"
                        type="text"
                        onChange={handleData}
                        invalid={formsData?.[digital_name]?.errors?.frst_nme}
                        invalidText={
                            formsData?.[digital_name]?.errors?.frst_nmeErrMsg
                        }
                    />

                    <TextInput
                        labelText="Middle Name:"
                        id="sec_nme"
                        name="sec_nme"
                        placeholder=""
                        value={formsData?.[digital_name]?.values?.sec_nme}
                        maxLength="40"
                        type="text"
                        onChange={handleData}
                        invalid={formsData?.[digital_name]?.errors?.sec_nme}
                        invalidText={
                            formsData?.[digital_name]?.errors?.sec_nmeErrMsg
                        }
                    />

                    <TextInput
                        className="required"
                        labelText="Last Name:"
                        id="surnme"
                        name="surnme"
                        placeholder=""
                        value={formsData?.[digital_name]?.values?.surnme}
                        maxLength="40"
                        type="text"
                        onChange={handleData}
                        invalid={formsData?.[digital_name]?.errors?.surnme}
                        invalidText={
                            formsData?.[digital_name]?.errors?.surnmeErrMsg
                        }
                    />

                    <TextInput
                        labelText="Suffix:"
                        id="sfx"
                        name="sfx"
                        placeholder=""
                        value={formsData?.[digital_name]?.values?.sfx}
                        maxLength="4"
                        type="text"
                        onChange={handleData}
                        invalid={formsData?.[digital_name]?.errors?.sfx}
                        invalidText={
                            formsData?.[digital_name]?.errors?.sfxErrMsg
                        }
                    />

                    <TooltipComponent
                        text="U.S. Social Security Number"
                        tooltip="Please enter your 9 digit SSN without dashes. If you do not have one, leave blank."
                    />

                    <TextInput.PasswordInput
                        className={''}
                        labelText="U.S. Social Security Number:"
                        id="tax_nbr"
                        name="tax_nbr"
                        placeholder="000000000"
                        value={
                            formsData?.[digital_name]?.values?.tax_nbr
                                ? formsData?.[digital_name]?.values?.tax_nbr
                                : ''
                        }
                        maxLength="9"
                        type="text"
                        helperText=""
                        hidePasswordLabel="Hide Enter your Social Security Number"
                        showPasswordLabel="Show Enter your Social Security Number"
                        onChange={(e) => handleData(e, 'tax_nbr')}
                        onFocus={handleJourneyAlert}
                        invalid={formsData?.[digital_name]?.errors?.tax_nbr}
                        invalidText={
                            formsData?.[digital_name]?.errors?.tax_nbrErrMsg
                        }
                    />

                    <TextInput.PasswordInput
                        className={''}
                        labelText="Re-enter your Social Security Number for confirmation:"
                        id="tax_nbrConfirm"
                        name="tax_nbrConfirm"
                        placeholder="000000000"
                        value={
                            formsData?.[digital_name]?.values?.tax_nbrConfirm
                                ? formsData?.[digital_name]?.values
                                      ?.tax_nbrConfirm
                                : ''
                        }
                        maxLength="9"
                        type="text"
                        helperText=""
                        hidePasswordLabel="Hide Re-enter your Social Security Number for confirmation"
                        showPasswordLabel="Show Re-enter your Social Security Number for confirmation"
                        onChange={(e) => handleData(e, 'tax_nbrConfirm')}
                        invalid={
                            formsData?.[digital_name]?.errors?.tax_nbrConfirm
                        }
                        invalidText={
                            formsData?.[digital_name]?.errors
                                ?.tax_nbrConfirmErrMsg
                        }
                    />

                    <DatePicker
                        className="required"
                        dateFormat="m/d/Y"
                        datePickerType="single"
                        onChange={(e) => handleData(e, 'brth_dte')}
                        onClose={(e) => handleData(e, 'brth_dte')}
                        value={formsData?.[digital_name]?.values?.brth_dte}
                    >
                        <DatePickerInput
                            className="required"
                            labelText="Date of Birth:"
                            id="brth_dte"
                            name="brth_dte"
                            placeholder="MM/DD/YYYY"
                            type="text"
                            invalid={
                                formsData?.[digital_name]?.errors?.brth_dte
                            }
                            invalidText={
                                formsData?.[digital_name]?.errors
                                    ?.brth_dteErrMsg
                            }
                        />
                    </DatePicker>

                    <Select
                        defaultValue={formsData?.[digital_name]?.values?.sex}
                        value={formsData?.[digital_name]?.values?.sex}
                        helperText=""
                        labelText="Gender:"
                        name="sex"
                        id="sex"
                        onChange={handleData}
                        invalid={formsData?.[digital_name]?.errors?.sex}
                        invalidText={
                            formsData?.[digital_name]?.errors?.sexErrMsg
                        }
                    >
                        <SelectItem text="Choose an option" value="" />
                        <SelectItem text="Male" value="M" />
                        <SelectItem text="Female" value="F" />
                    </Select>

                    <Select
                        defaultValue={
                            formsData?.[digital_name]?.values?.race_cde
                        }
                        value={formsData?.[digital_name]?.values?.race_cde}
                        labelText="Race:"
                        name="race_cde"
                        id="race_cde"
                        onChange={handleData}
                        invalid={formsData?.[digital_name]?.errors?.race_cde}
                        invalidText={
                            formsData?.[digital_name]?.errors?.race_cdeErrMsg
                        }
                    >
                        <SelectItem text="Choose an option" value="" />
                        <SelectItem text="Not Provided" value="9" />
                        <SelectItem text="White" value="1" />
                        <SelectItem text="Black" value="2" />
                        <SelectItem text="Native" value="3" />
                        <SelectItem text="Asian" value="4" />
                        <SelectItem text="Other" value="5" />
                        <SelectItem text="Hispanic" value="6" />
                    </Select>

                    <AddFormComponent />

                    <NotificationComponent notification={notification} />

                    <div className="button-group">
                        <div className="button-group-item">
                            <ButtonComponent
                                click={handleSubmit}
                                title="Save Progress"
                            />
                        </div>
                    </div>
                </Form>
            </>
        )
    } else if (formsData[digital_name].auxiliary.addtForms?.length > 0) {
        return (
            <>
                <Form onSubmit={handleSubmit}>
                    <TextInput
                        labelText="Title:"
                        id="ttl"
                        name="ttl"
                        placeholder=""
                        value={formsData?.[digital_name]?.values?.ttl}
                        maxLength="4"
                        type="text"
                        onChange={handleData}
                        invalid={formsData?.[digital_name]?.errors?.ttl}
                        invalidText={
                            formsData?.[digital_name]?.errors?.ttlErrMsg
                        }
                    />

                    <TextInput
                        className="required"
                        labelText="First Name:"
                        id="frst_nme"
                        name="frst_nme"
                        placeholder=""
                        value={formsData?.[digital_name]?.values?.frst_nme}
                        maxLength="16"
                        type="text"
                        onChange={handleData}
                        invalid={formsData?.[digital_name]?.errors?.frst_nme}
                        invalidText={
                            formsData?.[digital_name]?.errors?.frst_nmeErrMsg
                        }
                    />

                    <TextInput
                        labelText="Middle Name:"
                        id="sec_nme"
                        name="sec_nme"
                        placeholder=""
                        value={formsData?.[digital_name]?.values?.sec_nme}
                        maxLength="40"
                        type="text"
                        onChange={handleData}
                        invalid={formsData?.[digital_name]?.errors?.sec_nme}
                        invalidText={
                            formsData?.[digital_name]?.errors?.sec_nmeErrMsg
                        }
                    />

                    <TextInput
                        className="required"
                        labelText="Last Name:"
                        id="surnme"
                        name="surnme"
                        placeholder=""
                        value={formsData?.[digital_name]?.values?.surnme}
                        maxLength="40"
                        type="text"
                        onChange={handleData}
                        invalid={formsData?.[digital_name]?.errors?.surnme}
                        invalidText={
                            formsData?.[digital_name]?.errors?.surnmeErrMsg
                        }
                    />

                    <TextInput
                        labelText="Suffix:"
                        id="sfx"
                        name="sfx"
                        placeholder=""
                        value={formsData?.[digital_name]?.values?.sfx}
                        maxLength="4"
                        type="text"
                        onChange={handleData}
                        invalid={formsData?.[digital_name]?.errors?.sfx}
                        invalidText={
                            formsData?.[digital_name]?.errors?.sfxErrMsg
                        }
                    />

                    <TooltipComponent
                        text="U.S. Social Security Number"
                        tooltip="Please enter your 9 digit SSN without dashes."
                    />

                    <TextInput.PasswordInput
                        className={''}
                        labelText="U.S. Social Security Number:"
                        id="tax_nbr"
                        name="tax_nbr"
                        placeholder="000000000"
                        value={
                            formsData?.[digital_name]?.values?.tax_nbr
                                ? formsData?.[digital_name]?.values?.tax_nbr
                                : ''
                        }
                        maxLength="9"
                        type="text"
                        helperText=""
                        hidePasswordLabel="Hide Enter your Social Security Number"
                        showPasswordLabel="Show Enter your Social Security Number"
                        onChange={(e) => handleData(e, 'tax_nbr')}
                        onFocus={handleJourneyAlert}
                        invalid={formsData?.[digital_name]?.errors?.tax_nbr}
                        invalidText={
                            formsData?.[digital_name]?.errors?.tax_nbrErrMsg
                        }
                    />

                    <TextInput.PasswordInput
                        className={''}
                        labelText="Re-enter your Social Security Number for confirmation:"
                        id="tax_nbrConfirm"
                        name="tax_nbrConfirm"
                        placeholder="000000000"
                        value={
                            formsData?.[digital_name]?.values?.tax_nbrConfirm
                                ? formsData?.[digital_name]?.values
                                      ?.tax_nbrConfirm
                                : ''
                        }
                        maxLength="9"
                        type="text"
                        helperText=""
                        hidePasswordLabel="Hide Re-enter your Social Security Number for confirmation"
                        showPasswordLabel="Show Re-enter your Social Security Number for confirmation"
                        onChange={(e) => handleData(e, 'tax_nbrConfirm')}
                        invalid={
                            formsData?.[digital_name]?.errors?.tax_nbrConfirm
                        }
                        invalidText={
                            formsData?.[digital_name]?.errors
                                ?.tax_nbrConfirmErrMsg
                        }
                    />

                    <DatePicker
                        className="required"
                        dateFormat="m/d/Y"
                        datePickerType="single"
                        onChange={(e) => handleData(e, 'brth_dte')}
                        onClose={(e) => handleData(e, 'brth_dte')}
                        value={formsData?.[digital_name]?.values?.brth_dte}
                    >
                        <DatePickerInput
                            className="required"
                            labelText="Date of Birth:"
                            id="brth_dte"
                            name="brth_dte"
                            placeholder="MM/DD/YYYY"
                            type="text"
                            invalid={
                                formsData?.[digital_name]?.errors?.brth_dte
                            }
                            invalidText={
                                formsData?.[digital_name]?.errors
                                    ?.brth_dteErrMsg
                            }
                        />
                    </DatePicker>

                    <Select
                        defaultValue={formsData?.[digital_name]?.values?.sex}
                        value={formsData?.[digital_name]?.values?.sex}
                        helperText=""
                        labelText="Gender:"
                        name="sex"
                        id="sex"
                        onChange={handleData}
                        invalid={formsData?.[digital_name]?.errors?.sex}
                        invalidText={
                            formsData?.[digital_name]?.errors?.sexErrMsg
                        }
                    >
                        <SelectItem text="Choose an option" value="" />
                        <SelectItem text="Male" value="M" />
                        <SelectItem text="Female" value="F" />
                    </Select>

                    <Select
                        defaultValue={
                            formsData?.[digital_name]?.values?.race_cde
                        }
                        value={formsData?.[digital_name]?.values?.race_cde}
                        labelText="Race:"
                        name="race_cde"
                        id="race_cde"
                        onChange={handleData}
                        invalid={formsData?.[digital_name]?.errors?.race_cde}
                        invalidText={
                            formsData?.[digital_name]?.errors?.race_cdeErrMsg
                        }
                    >
                        <SelectItem text="Choose an option" value="" />
                        <SelectItem text="Not Provided" value="9" />
                        <SelectItem text="White" value="1" />
                        <SelectItem text="Black" value="2" />
                        <SelectItem text="Native" value="3" />
                        <SelectItem text="Asian" value="4" />
                        <SelectItem text="Other" value="5" />
                        <SelectItem text="Hispanic" value="6" />
                    </Select>

                    {formsData[digital_name].auxiliary.addtForms.map((el) => (
                        <AdditionalNameAndPersonalDetailsComponent
                            key={el}
                            number={el}
                            handleData={handleData}
                            handleSubtractAdditionalNameAndPersonalDetailsComponent={
                                handleSubtractAdditionalNameAndPersonalDetailsComponent
                            }
                        />
                    ))}

                    <AddFormComponent />

                    <NotificationComponent notification={notification} />

                    <div className="button-group">
                        <div className="button-group-item">
                            <ButtonComponent
                                click={handleSubmit}
                                title="Save Progress"
                            />
                        </div>
                    </div>
                </Form>
            </>
        )
    }
}

export default NameAndPersonalDetailsForm
